import React, { useState, useMemo } from 'react';
import { ChevronDown, Search, Check, X, Layers } from 'lucide-react';
import { PROJECT_DOMAINS } from '../../data/projectDomains';

/**
 * Multi-select for project / work domains. Shared by every job form so the
 * field looks and behaves identically everywhere.
 *
 * value    — array of selected domain strings
 * onChange — called with the new array
 */
const ProjectDomainPicker = ({ value = [], onChange, label = 'Project Domains', hint = 'Select one or more domains this role works in.' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const selected = Array.isArray(value) ? value : [];

  const options = useMemo(() => {
    const s = q.trim().toLowerCase();
    // Anything already selected that is not in the master list (older data)
    // still shows, so it can be unticked.
    const all = Array.from(new Set([...PROJECT_DOMAINS, ...selected]));
    return s ? all.filter(d => d.toLowerCase().includes(s)) : all;
  }, [q, selected]);

  const toggle = (d) => {
    onChange(selected.includes(d) ? selected.filter(x => x !== d) : [...selected, d]);
  };

  return (
    <div className="space-y-1.5">
      <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
        {label} <span className="font-medium normal-case tracking-normal text-gray-400">(Optional)</span>
      </label>

      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="w-full min-h-[46px] flex items-center justify-between gap-2 px-3.5 py-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 bg-gray-50 dark:bg-[#001430] text-left hover:border-[#1a3884]/40 focus:outline-none focus:ring-2 focus:ring-[#1a3884] transition-all"
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Layers size={15} className="text-gray-400 shrink-0" />
            {selected.length === 0 ? (
              <span className="text-sm text-gray-400">Select project domains...</span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {selected.map(d => (
                  <span
                    key={d}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1a3884] dark:text-[#4f7dff] bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 border border-[#1a3884]/15 dark:border-[#4f7dff]/25 px-2 py-0.5 rounded-md"
                  >
                    {d}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); toggle(d); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggle(d); } }}
                      className="hover:text-red-500 cursor-pointer"
                      aria-label={`Remove ${d}`}
                    >
                      <X size={11} />
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <ChevronDown size={15} className={`text-gray-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-xl border border-gray-200 dark:border-[#1a3884]/40 bg-white dark:bg-[#001f40] shadow-xl overflow-hidden">
              <div className="p-2 border-b border-gray-100 dark:border-[#1a3884]/25">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    autoFocus
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search domains..."
                    className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-[#1a3884]/30 bg-gray-50 dark:bg-[#001430] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1a3884]"
                  />
                </div>
              </div>
              <div className="px-3 py-1.5 bg-gray-50/70 dark:bg-slate-900/50 flex items-center justify-between text-[11px] text-gray-500 dark:text-slate-400 font-semibold border-b border-gray-100 dark:border-slate-800">
                <span>Domains</span>
                <span>{selected.length} selected</span>
              </div>
              <div className="max-h-56 overflow-y-auto custom-scrollbar py-1">
                {options.length === 0 ? (
                  <div className="text-center py-3 text-xs text-gray-500">No matching domain.</div>
                ) : options.map(d => {
                  const isSel = selected.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggle(d)}
                      className={`w-full text-left px-4 py-2 text-xs font-semibold flex items-center justify-between transition-colors ${
                        isSel
                          ? 'text-[#1a3884] dark:text-[#4f7dff] bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 font-bold'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-[#1a3884]/5 dark:hover:bg-[#4f7dff]/10'
                      }`}
                    >
                      <span>{d}</span>
                      {isSel && <Check size={14} className="text-[#1a3884] dark:text-[#4f7dff]" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
      {hint && <p className="text-[10px] text-gray-400 dark:text-slate-500">{hint}</p>}
    </div>
  );
};

export default ProjectDomainPicker;
